import { Injectable } from '@angular/core';
import { State, Selector, Action, StateContext, createSelector } from '@ngxs/store';
import { Observable, of, forkJoin, throwError } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';
import { PulseService } from 'ng2-app/pulse/pulse.service';
import { normalizeRealTimeScores, normalizeHistoricalScores } from 'ng2-app/pulse/pulse.utils';
import { LenderUserRealTimeScores, LenderUserHistoricalScores } from 'ng2-app/models/pulse/scores.model';
import { GetScoresByScoringGroupId } from 'ng2-app/store/pulse/pulse.action';
import {
  QueryStateBase,
  setQueryLoading,
  setQuerySuccess,
  setQueryError,
  isQueryLoading,
  isQueryFetching,
} from 'ng2-app/store/query-state.model';

export interface LeaderboardScoreGroup {
  realTimeScores: LenderUserRealTimeScores[];
  historicalScores: LenderUserHistoricalScores[];
}

export type LeaderboardLastSync = {
  realTimeScores: Date | null;
  historicalScores: Date | null;
};

export interface LeaderboardQueryEntry extends QueryStateBase<LeaderboardScoreGroup> {
  lastSync: LeaderboardLastSync;
}

export interface PulseLeaderboardStateModel {
  queries: { [groupId: number]: LeaderboardQueryEntry };
}

const DEFAULT_DATA: LeaderboardScoreGroup = { realTimeScores: [], historicalScores: [] };
export const LEADERBOARD_REALTIME_TTL = 1000 * 60 * 5; // 5 minutes

function createLeaderboardQuery(): LeaderboardQueryEntry {
  return {
    data: DEFAULT_DATA,
    status: 'idle',
    isFetching: false,
    error: null,
    lastSync: { realTimeScores: null, historicalScores: null },
  };
}

function isSameDay(date: Date): boolean {
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate() &&
    now.getHours() > 3 // Cron for historical scores runs at 2am
  );
}

@State<PulseLeaderboardStateModel>({
  name: 'pulseLeaderboard',
  defaults: {
    queries: {},
  },
})
@Injectable()
export class PulseLeaderboardState {
  constructor(private pulseService: PulseService) {}

  // --- Selectors ---

  @Selector()
  static queries(state: PulseLeaderboardStateModel) {
    return state?.queries ?? null;
  }

  static leaderboardQuery(groupId: number) {
    return createSelector(
      [PulseLeaderboardState.queries],
      (queries: PulseLeaderboardStateModel['queries']) =>
        queries[groupId] ?? createLeaderboardQuery()
    );
  }

  static leaderboardData(groupId: number) {
    return createSelector(
      [PulseLeaderboardState.leaderboardQuery(groupId)],
      (query: LeaderboardQueryEntry) => query.data
    );
  }

  static leaderboardIsLoading(groupId: number) {
    return createSelector(
      [PulseLeaderboardState.leaderboardQuery(groupId)],
      (query: LeaderboardQueryEntry) => isQueryLoading(query)
    );
  }

  static leaderboardIsFetching(groupId: number) {
    return createSelector(
      [PulseLeaderboardState.leaderboardQuery(groupId)],
      (query: LeaderboardQueryEntry) => isQueryFetching(query)
    );
  }

  static leaderboardLastSync(groupId: number) {
    return createSelector(
      [PulseLeaderboardState.leaderboardQuery(groupId)],
      (query: LeaderboardQueryEntry) => query.lastSync.realTimeScores
    );
  }

  // --- Actions ---

  @Action(GetScoresByScoringGroupId)
  getScoresByScoringGroupId(
    ctx: StateContext<PulseLeaderboardStateModel>,
    { scoringGroupId, overrideCache = false }: GetScoresByScoringGroupId
  ): Observable<LeaderboardScoreGroup> {
    const state = ctx.getState();
    const query = state?.queries[scoringGroupId] ?? createLeaderboardQuery();

    const needsRealTime = !this.isRealTimeCacheValid(query, overrideCache);
    const needsHistorical = !this.isHistoricalCacheValid(query);

    if (!needsRealTime && !needsHistorical) {
      return of(query.data);
    }

    // Set loading state
    ctx.patchState({
      queries: {
        ...state?.queries,
        [scoringGroupId]: { ...query, ...setQueryLoading(query) },
      },
    });

    const realTimeScores$ = needsRealTime
      ? this.pulseService.getRealTimeScoresByScoreGroupId(scoringGroupId)
      : of(query.data.realTimeScores);

    const historicalScores$ = needsHistorical
      ? this.pulseService.getHistoricalScoresByScoreGroupId(scoringGroupId)
      : of(query.data.historicalScores);

    return forkJoin({ realTimeScores: realTimeScores$, historicalScores: historicalScores$ }).pipe(
      tap(data => {
        for (const user of data.realTimeScores) normalizeRealTimeScores(user);
        normalizeHistoricalScores(data.historicalScores);
        const now = new Date();
        const currentQuery = ctx.getState().queries[scoringGroupId] ?? createLeaderboardQuery();
        ctx.patchState({
          queries: {
            ...ctx.getState().queries,
            [scoringGroupId]: {
              ...currentQuery,
              ...setQuerySuccess(data),
              lastSync: {
                realTimeScores: needsRealTime ? now : currentQuery.lastSync.realTimeScores,
                historicalScores: needsHistorical ? now : currentQuery.lastSync.historicalScores,
              },
            },
          },
        });
      }),
      map(data => data),
      catchError(error => {
        const currentQuery = ctx.getState().queries[scoringGroupId] ?? createLeaderboardQuery();
        ctx.patchState({
          queries: {
            ...ctx.getState().queries,
            [scoringGroupId]: { ...currentQuery, ...setQueryError(error) },
          },
        });
        return throwError(() => error);
      })
    );
  }

  // --- Cache helpers ---

  private isRealTimeCacheValid(query: LeaderboardQueryEntry, forceRefresh: boolean): boolean {
    if (forceRefresh) return false;
    const lastSync = query.lastSync.realTimeScores;
    if (!lastSync) return false;
    return lastSync.getTime() + LEADERBOARD_REALTIME_TTL > Date.now();
  }

  private isHistoricalCacheValid(query: LeaderboardQueryEntry): boolean {
    const lastSync = query.lastSync.historicalScores;
    if (!lastSync) return false;
    return isSameDay(lastSync);
  }
}
